import { Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { CommandPalette } from '@/components/CommandPalette';
import { DetectiveFooter } from '@/components/DetectiveFooter';
import { MobileTopBar } from '@/components/MobileTopBar';
import { Sidebar } from '@/components/Sidebar';
import { visibleNavItems } from '@/constants/navItems';
import { useAuth } from '@/contexts/AuthContext';
import { useHotkey } from '@/hooks/useHotkey';
import { useSidebarState } from '@/hooks/useSidebarState';

export function Layout(): ReactElement {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { collapsed, mode, toggle } = useSidebarState();
  const { user } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Shift+Arrow cycles between sidebar tabs visible to the current user
  // (admin sees /memory, non-admin doesn't). Starts from whichever item
  // best matches the current path; if none match (e.g. on a detail
  // route), pretend we're "before" the first item so Down lands on the
  // first tab and Up lands on the last.
  const cycleNav = (direction: 1 | -1): void => {
    const items = visibleNavItems(user?.email);
    if (items.length === 0) return;
    const currentIndex = items.findIndex(
      (item) =>
        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
    );
    const start = currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
    const next = (start + direction + items.length) % items.length;
    const target = items[next];
    if (target) navigate(target.to);
  };

  // Global hotkeys
  useHotkey('cmd+k', () => setPaletteOpen((v) => !v));
  useHotkey('ctrl+k', () => setPaletteOpen((v) => !v));
  useHotkey('cmd+\\', () => toggle());
  useHotkey('shift+arrowdown', () => cycleNav(1));
  useHotkey('shift+arrowup', () => cycleNav(-1));

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  if (mode === 'drawer') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <MobileTopBar
          onOpenDrawer={() => setDrawerOpen(true)}
          onOpenCommandPalette={() => setPaletteOpen(true)}
        />
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundColor: 'background.deeper',
              backgroundImage: 'none',
            },
          }}
        >
          <Sidebar
            collapsed={false}
            onToggle={() => setDrawerOpen(false)}
            onNavigate={() => setDrawerOpen(false)}
          />
        </Drawer>
        {/* Content padded at the bottom so the floating buddy doesn't overlap the last lines */}
        {/* overflowX: 'clip' (not 'hidden') clips wide content WITHOUT creating a
            scroll container, so descendant position:sticky still pins to the window. */}
        <Box component="main" sx={{ flex: 1, p: 2, pb: '96px', overflowX: 'clip', minWidth: 0 }}>
          <Outlet />
        </Box>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <DetectiveFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      {/* Content padded at the bottom so the floating buddy doesn't overlap the last lines */}
      {/* overflowX: 'clip' (not 'hidden') clips wide content WITHOUT creating a
          scroll container, so descendant position:sticky still pins to the window. */}
      <Box component="main" sx={{ flex: 1, p: 3, pb: '104px', minWidth: 0, overflowX: 'clip' }}>
        <Outlet />
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <DetectiveFooter />
    </Box>
  );
}
